import React, { memo, useContext } from 'react'
import { GlobalOutlined } from '@ant-design/icons'
import { Avatar } from 'antd'
import {
  IwmFlagEs,
  IwmFlagFr,
  IwmFlagIt,
  IwmFlagLu,
  IwmFlagMc,
} from '@iwm-ds/react'
import CountryEnum from '../domain/country.enum'
import { CountryContext } from '../context/CountryContext'

interface Props {
  size?: number
  className?: string
  country: string | CountryEnum
}

const CountryIcon = ({ size = 20, className, country }: Props): JSX.Element => {
  const { countries } = useContext(CountryContext)
  
  // Props for IWM components - circular style
  const iwmProps = {
    alt: 'country flag',
    height: { size },
    width: { size },
    className: className,
    style: { borderRadius: '50%', overflow: 'hidden', objectFit: 'cover' as const },
  }

  // Legacy hardcoded mapping for backward compatibility with IWM components
  const legacyCountryMap: Record<string, JSX.Element> = {
    [CountryEnum.FRANCE]: <IwmFlagFr {...iwmProps} />,
    [CountryEnum.ITALIE]: <IwmFlagIt {...iwmProps} />,
    [CountryEnum.ESPAGNE]: <IwmFlagEs {...iwmProps} />,
    [CountryEnum.LUXEMBOURG]: <IwmFlagLu {...iwmProps} />,
    [CountryEnum.MONACO]: <IwmFlagMc {...iwmProps} />,
  }

  // Check if it's a legacy country with IWM component
  if (legacyCountryMap[country]) {
    return <span key={`country-${country}`}>{legacyCountryMap[country]}</span>
  }

  // For dynamic countries, find the country code and use CDN with circular avatar
  const foundCountry = countries.find(c => c.name === country || c.code === country)
  
  if (foundCountry && foundCountry.code) {
    const code = foundCountry.code.toLowerCase()
    
    return (
      <Avatar
        key={`country-${country}`}
        size={size}
        className={className}
        src={`https://flagcdn.com/w80/${code}.png`}
        srcSet={`https://flagcdn.com/w160/${code}.png 2x`}
        alt={`${foundCountry.name} flag`}
        style={{ 
          objectFit: 'cover',
          border: '1px solid #d9d9d9'
        }}
        icon={<GlobalOutlined />}
      />
    )
  }

  // Ultimate fallback: globe icon in circular avatar
  return (
    <Avatar
      key={`country-${country}`}
      size={size}
      className={className}
      icon={<GlobalOutlined />}
      style={{ backgroundColor: '#f0f0f0', color: '#8c8c8c' }}
    />
  )
}

export default memo(CountryIcon)
